import { AmountDisplay } from '@/design-system/amount-display/amount-display';
import { Inline } from '@/design-system/inline/inline';
import { CardView } from '@/types/entity-types';
import { getAccountDisplayName } from '@/utils/accounts/get-account-display-name';
import { createColumnHelper } from '@tanstack/react-table';
import { getLastFourDigits } from '../../utils/get-last-four-digits';
import { getStatusLabel } from '../../utils/get-status-label';
import { CardActionsMenu } from '../card-actions-menu';
import { CardStatusBadge } from '../card-status-badge';
import { MiniCard } from '../mini-card';

const columnHelper = createColumnHelper<CardView>();

export const cardsTableColumns = [
  columnHelper.accessor((row) => `••${getLastFourDigits(row.maskedPan)} · ${row.displayName}`, {
    id: 'card',
    header: 'Card',
    cell: ({ row }) => {
      const displayText = `••${getLastFourDigits(row.original.maskedPan)} · ${row.original.displayName}`;

      return (
        <Inline space={2} align="center" wrap="nowrap">
          <MiniCard />
          <span className="truncate text-sm">{displayText}</span>
        </Inline>
      );
    },
    size: 65,
  }),
  columnHelper.accessor((row) => `${row.holderFirstName} ${row.holderLastName}`, {
    id: 'cardholder',
    header: 'Cardholder',
    cell: ({ row }) => (
      <span className="text-sm">
        {row.original.holderFirstName} {row.original.holderLastName}
      </span>
    ),
    size: 45,
  }),
  columnHelper.accessor((row) => getStatusLabel(row.status), {
    id: 'status',
    header: () => <div className="w-full text-center">Status</div>,
    cell: ({ row }) => (
      <div className="flex justify-center">
        <CardStatusBadge status={row.original.status} />
      </div>
    ),
    filterFn: (row, _id, filterValue) => {
      const rowDisplayValue = getStatusLabel(row.original.status);
      return Array.isArray(filterValue) && filterValue.includes(rowDisplayValue);
    },
    size: 35,
  }),
  columnHelper.accessor(
    (row) => {
      const account = row.account;
      if (!account) return 'Unknown';
      return getAccountDisplayName(account);
    },
    {
      id: 'account',
      header: 'Account',
      cell: ({ row }) => {
        const account = row.original.account;
        if (!account) return <span className="text-sm">Unknown</span>;
        const accountName = getAccountDisplayName(account);
        return <span className="text-sm">{accountName}</span>;
      },
      size: 45,
    },
  ),
  columnHelper.accessor('currency', {
    id: 'spentThisMonth',
    header: () => <div className="w-full text-right">Spent this month</div>,
    cell: ({ row }) => {
      // TODO: Replace with actual spent amount when card activity data is available
      const spentThisMonth = 0;

      return (
        <div className="text-right">
          <AmountDisplay normalizedAmount={-spentThisMonth} currency={row.original.currency} />
        </div>
      );
    },
    size: 55,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => null,
    cell: ({ row, table }) => {
      const { onEditNickname, onCancelCard } = table.options.meta ?? {};

      return (
        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
          <CardActionsMenu
            cardStatus={row.original.status}
            onEditNickname={onEditNickname ? () => onEditNickname(row.original) : () => {}}
            onCancelCard={onCancelCard ? () => onCancelCard(row.original) : undefined}
          />
        </div>
      );
    },
    enableSorting: false,
    enableGlobalFilter: false,
    size: 1,
  }),
];
